/**
 * Per-route SEO metadata and structured data.
 *
 * The app is a client-rendered SPA, so there is no server to emit per-page
 * <head>. RouteSeo applies these on navigation (Google renders JS, so this is
 * indexed). Canonicals always point at the production origin -- never the /v2/
 * staging path -- so a page served from staging still declares the real URL and
 * cannot be indexed as a duplicate.
 *
 * URIs are fixed: these pages are already indexed at the paths below and must
 * not change.
 */

import type { FaqEntry } from './faq'

export const PROD_ORIGIN = 'https://dnsleaktest.org'
export const OG_IMAGE = `${PROD_ORIGIN}/og.png`
const SITE_NAME = 'dnsleaktest.org'

export interface PageSeo {
    title: string
    description: string
    /** Keep out of the index: dynamic or thin pages. */
    noindex?: boolean
    /** JSON-LD blocks specific to this page, on top of the sitewide ones. */
    jsonLd?: Record<string, unknown>[]
    /** Structured data too large to keep on the critical path. Fetched after
        the head is written, and merged into the graph when it arrives. */
    lazyJsonLd?: () => Promise<Record<string, unknown>[]>
}

//#region sitewide structured data
const organization = {
    '@type': 'Organization',
    '@id': `${PROD_ORIGIN}/#organization`,
    name: 'DEF24 Inc.',
    url: PROD_ORIGIN,
    logo: `${PROD_ORIGIN}/favicon.png`,
}

const website = {
    '@type': 'WebSite',
    '@id': `${PROD_ORIGIN}/#website`,
    name: SITE_NAME,
    url: PROD_ORIGIN,
    publisher: { '@id': `${PROD_ORIGIN}/#organization` },
    inLanguage: 'en',
}

/** Included on every page so the site/org graph is always present. */
export const sitewideJsonLd: Record<string, unknown>[] = [organization, website]
//#endregion

//#region page-specific structured data
const dnsLeakToolApp = {
    '@type': 'WebApplication',
    name: 'DNS Leak Test',
    url: `${PROD_ORIGIN}/dns-leak-test`,
    applicationCategory: 'SecurityApplication',
    operatingSystem: 'Any',
    browserRequirements: 'Requires JavaScript',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: { '@id': `${PROD_ORIGIN}/#organization` },
}

/* A FAQ built from the home copy. The questions are the section headings the
   page already shows, so the structured data matches the visible content --
   which is what Google's FAQ guidelines require. */
const homeFaq = {
    '@type': 'FAQPage',
    mainEntity: [
        {
            '@type': 'Question',
            name: 'What is a DNS leak?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'A DNS leak is when your DNS queries go somewhere you did not choose. DNS settings live in your router, operating system, browser and sometimes individual apps; when they disagree, some queries quietly go elsewhere. Misconfiguration, badly behaved software, or a network intercepting DNS can all cause it.',
            },
        },
        {
            '@type': 'Question',
            name: 'Why does DNS privacy matter?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Almost nothing works without DNS translating names into IP addresses first, and most DNS traffic still travels without encryption. Every network between you and your resolver can see which sites you are about to visit, which opens the door to profiling, spoofing, man-in-the-middle attacks, filtering and phishing.',
            },
        },
        {
            '@type': 'Question',
            name: 'Is this DNS leak test free, and who runs it?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, it is free and there is nothing to buy. Most DNS leak tests are run by VPN companies and conclude that you should buy a VPN. We do not sell one. A DNS leak is usually a settings change, not a subscription.',
            },
        },
    ],
}

const dohApp = {
    '@type': 'WebApplication',
    name: 'DNS-over-HTTPS Test',
    url: `${PROD_ORIGIN}/dns-over-https`,
    applicationCategory: 'SecurityApplication',
    operatingSystem: 'Any',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: { '@id': `${PROD_ORIGIN}/#organization` },
}

const resolverDataset = {
    '@type': 'Dataset',
    name: 'Validated public DNS-over-HTTPS resolvers',
    description:
        'A regularly revalidated directory of public DoH resolvers with country, city, continent, latency and CORS support.',
    url: `${PROD_ORIGIN}/doh-resolvers`,
    license: 'https://creativecommons.org/licenses/by/4.0/',
    isAccessibleForFree: true,
    creator: { '@id': `${PROD_ORIGIN}/#organization` },
    distribution: [
        { '@type': 'DataDownload', encodingFormat: 'application/json', contentUrl: `${PROD_ORIGIN}/api/doh/resolvers` },
    ],
}

/**
 * FAQPage built from the same entries the page renders.
 *
 * Google requires the structured data to match the visible content, so this is
 * derived rather than hand-written -- a hand-written copy would drift the first
 * time an answer was edited.
 */
export function faqJsonLd(entries: readonly FaqEntry[]): Record<string, unknown> {
    return {
        '@type': 'FAQPage',
        mainEntity: entries.map((entry) => ({
            '@type': 'Question',
            name: entry.question,
            acceptedAnswer: { '@type': 'Answer', text: entry.answer.join(' ') },
        })),
    }
}

const fixingArticle = {
    '@type': 'Article',
    headline: 'How to fix a DNS leak',
    author: { '@id': `${PROD_ORIGIN}/#organization` },
    publisher: { '@id': `${PROD_ORIGIN}/#organization` },
    mainEntityOfPage: `${PROD_ORIGIN}/fixing-dns-leak`,
}
//#endregion

//#region route table
/** Exact-path routes. Dynamic and unknown paths are handled in RouteSeo. */
export const routeSeo: Record<string, PageSeo> = {
    '/': {
        title: 'DNS Leak Test — Is Your VPN Leaking DNS?',
        description:
            'Free DNS leak test. See which DNS resolvers actually answer your lookups, who operates them, and whether your VPN is leaking your DNS.',
        jsonLd: [dnsLeakToolApp, homeFaq],
    },
    '/dns-leak-test': {
        title: 'Run a DNS Leak Test — Live Results in Your Browser',
        description:
            'Run a live DNS leak test. We send 50 lookups to a unique subdomain and reveal every resolver that answered — and the organisations behind them.',
        jsonLd: [dnsLeakToolApp],
    },
    '/dns-over-https': {
        title: 'DNS-over-HTTPS Test — Measure & Enable DoH',
        description:
            'Measure DNS-over-HTTPS latency to public resolvers from your browser, and learn how to enable encrypted DNS in Firefox, Chrome, Edge, Brave and Opera.',
        jsonLd: [dohApp],
    },
    '/doh-resolvers': {
        title: 'Public DoH Resolvers — Filter, Compare & Download',
        description:
            'Browse every validated public DNS-over-HTTPS resolver: filter by country, continent, latency and CORS support, compare them, and download the list as JSON or CSV.',
        jsonLd: [resolverDataset],
    },
    '/fixing-dns-leak': {
        title: 'How to Fix a DNS Leak — A Practical Guide',
        description:
            "A DNS leak sends your queries somewhere you did not choose. Learn why leaks happen and how to fix them across your router, operating system and browser.",
        jsonLd: [fixingArticle],
    },
    '/faq': {
        title: 'DNS FAQ — Leaks, VPNs, DoH and Encrypted DNS',
        description:
            'Short answers to the questions a DNS leak test raises: what a leak is, why a VPN still leaks, DoH vs DoT vs DNSSEC, what your resolver can see, and how our test works.',
        // The answers are ~30KB of copy. Importing them here would put the whole
        // FAQ in the entry bundle for every visitor, to describe one page.
        lazyJsonLd: () => import('./faq').then((m) => [faqJsonLd(m.faqEntries)]),
    },
    '/privacy-policy': {
        title: 'Privacy Policy — dnsleaktest.org',
        description:
            'How dnsleaktest.org handles your data, and the terms that apply to your use of the service.',
    },
}

/** A shared leak-test result: unbounded dynamic URLs and other people's data,
    so it is deliberately kept out of the index. */
export const sharedResultSeo: PageSeo = {
    title: 'Shared DNS Leak Test Result — dnsleaktest.org',
    description: 'A shared DNS leak test result.',
    noindex: true,
}

export const notFoundSeo: PageSeo = {
    title: 'Page not found — dnsleaktest.org',
    description: 'That page does not exist.',
    noindex: true,
}
//#endregion
